import React from 'react';
import { Edit, Trash2, User, GraduationCap, Clock, Calendar, CheckCircle, XCircle } from 'lucide-react';

const PonctualiteTable = ({
  activeTab,
  absencesEtudiants,
  absencesEnseignants,
  handleEdit,
  handleDelete,
  formatDate
}) => {
  const currentData = activeTab === 'etudiants' ? absencesEtudiants : absencesEnseignants;

  if (currentData.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="max-w-md mx-auto">
          <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Calendar className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Aucune absence enregistrée
          </h3>
          <p className="text-gray-600 mb-6">
            Commencez par enregistrer une absence pour {activeTab === 'etudiants' ? 'un étudiant' : 'un enseignant'}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <div className="flex items-center space-x-2">
                  {activeTab === 'etudiants' ? (
                    <GraduationCap className="h-4 w-4" />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                  <span>{activeTab === 'etudiants' ? 'Étudiant' : 'Enseignant'}</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Classe
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Module
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4" />
                  <span>Volume Horaire</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4" />
                  <span>Date</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Motif
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentData.map((absence, index) => (
              <tr 
                key={absence.id} 
                className={`hover:bg-gray-50 transition-colors duration-150 ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                }`}
              >
                {/* Personne */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-[#202654] flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {activeTab === 'etudiants' 
                            ? `${absence.etudiant?.nom?.charAt(0) || ''}${absence.etudiant?.prenom?.charAt(0) || ''}`
                            : `${absence.enseignant?.nom?.charAt(0) || ''}${absence.enseignant?.prenom?.charAt(0) || ''}`
                          }
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {activeTab === 'etudiants' 
                          ? `${absence.etudiant?.nom || ''} ${absence.etudiant?.prenom || ''}`
                          : `${absence.enseignant?.nom || ''} ${absence.enseignant?.prenom || ''}`
                        }
                      </div>
                      {activeTab === 'etudiants' && absence.etudiant?.matricule && (
                        <div className="text-sm text-gray-500">
                          {absence.etudiant.matricule}
                        </div>
                      )}
                    </div>
                  </div>
                </td>

                {/* Classe */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-[#202654]">
                    {absence.matiere?.classe?.libelle || absence.classe_nom || 'N/A'}
                  </span>
                </td>

                {/* Module */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {absence.matiere?.module?.libelle || absence.matiere?.libelle || 'Aucun module'}
                  </div>
                </td>

                {/* Volume Horaire */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm font-medium text-gray-900">
                      {absence.volume_horaire}h
                    </span>
                  </div>
                </td>

                {/* Statut */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    {absence.absence ? (
                      <>
                        <XCircle className="h-4 w-4 text-red-500" />
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Absent
                        </span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Présent
                        </span>
                      </>
                    )}
                  </div>
                </td>

                {/* Date */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {formatDate(absence.date_absence || absence.created_at)}
                  </div>
                </td>

                {/* Motif */}
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 max-w-xs truncate" title={absence.motif}>
                    {absence.motif || 'Aucun motif'}
                  </div>
                </td>

                {/* Actions */}
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => handleEdit(absence)}
                      className="p-2 text-[#202654] hover:text-[#202654]/80 hover:bg-gray-100 rounded-lg transition-all duration-200"
                      title="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(absence.id)}
                      className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-all duration-200"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PonctualiteTable; 